import React, { useState } from 'react';
import styled from 'styled-components';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import PropTypes from 'prop-types';

const ImageBox = styled.div`
  width: 100%;
  margin: auto;
  margin-top: 2rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 8px 16px var(--john-bg-shadow);
  position: relative;
  display: grid;
  overflow-x: auto;
  align-items: center;
  justify-content: center;
  img {
    width: 70%;
    height: auto;
    margin: auto;
  }
`;

const VideoBox = styled(ImageBox)`
  display: block;
  .video-box {
    position: relative;
    width: 70%;
    padding-bottom: 39.375%;
    height: 0;
    margin: 0 auto;
  }
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;
const LongImageBox = styled(ImageBox)`
  max-height: 350px;
  overflow-y: scroll;
`;

const SliderBox = styled(ImageBox)`
  max-height: 400px !important;
  aspect-ratio: 3 / 2;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  overflow: hidden;
  background-color: var(--john-text-pretty-shadow);
  border-radius: 12px 12px 0 0;
  margin-top: auto;
  margin-bottom: auto;
`;

const SlidingImageBox = styled.div`
  box-shadow: 0 8px 16px var(--john-bg-shadow);
  background-color: var(--john-bg-mild);
  margin: auto;
  width: 70%;
  overflow: hidden;
  height: 100%;
  overflow-y: scroll;
  display: flex;
  justify-content: center; /* horizontal centering if fits */
  align-items: center; /* vertical centering if fits */
  padding-left: 4px;

  ::-webkit-scrollbar {
    background: transparent; /* no track space */
    width: 4px;
    position: absolute; /* overlays content (visual only) */
  }
  ::-webkit-scrollbar-thumb {
    background-color: var(--john-text-moderate);
    border-radius: 2px;
  }

  .scroll-box::-webkit-scrollbar-track {
    background: transparent; /* transparent track */
  }

  img {
    margin: auto;
    object-fit: cover; /* fit inside preserving aspect ratio */
    width: 100%;
    // position: absolute;
    // top: 50%;
    // left: 50%;
    // transform: translate(-50%, -50%);
  }
`;

const ImageDescription = styled.div`
  position: sticky !important;
  bottom: 0;
  color: white;
  background: rgba(0, 0, 0, 0.5);
  padding: 18px 32px;
  border-radius: 0 0 12px 12px;
  font-size: 1rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.18);
  backdrop-filter: blur(2px);
  text-align: center;
  z-index: 10;
`;

const ArrowButton = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(calc(-50% - 30px));
  z-index: 10;
  cursor: pointer;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.15);
  transition: background 0.2s;

  ${({ theme }) => theme.mixins.bigButton};
  padding: 0.75rem;

  &.prev {
    left: 1.5rem;
  }

  &.next {
    right: 1.5rem;
  }
`;

const StyledSlider = styled(Slider)`
  .slick-dots {
    li {
      margin: 0 5px;
      button {
        &:before {
          color: var(--john-text-moderate);
          font-size: 12px;
          opacity: 0.7 !important;
        }
      }

      &.slick-active button:before {
        transition: color 0.3s ease;
        color: var(--john-photo-overlay);
      }
    }
  }
`;

function NextArrow(props) {
  const { onClick } = props;
  return (
    <ArrowButton className="next" onClick={onClick}>
      <FaChevronRight className="text-xl" />
    </ArrowButton>
  );
}

function PrevArrow(props) {
  const { onClick } = props;
  return (
    <ArrowButton className="prev" onClick={onClick}>
      <FaChevronLeft className="text-xl" />
    </ArrowButton>
  );
}

export function LongImage({ src, alt, desc, href }) {
  return (
    <LongImageBox>
      {<img src={src} alt={alt}></img>}
      <ImageDescription>
        <div>
          {href ? (
            <span>
              {' '}
              {`${desc} `}
              <a href={href}>(source)</a>
            </span>
          ) : (
            desc
          )}
        </div>
      </ImageDescription>
    </LongImageBox>
  );
}

export function NormalImage({ src, alt, desc, href }) {
  return (
    <ImageBox>
      <img src={src} alt={alt}></img>
      <ImageDescription>
        <div>
          {href ? (
            <span>
              {' '}
              {`${desc} `}
              <a href={href}>(source)</a>
            </span>
          ) : (
            desc
          )}
        </div>
      </ImageDescription>
    </ImageBox>
  );
}

export function NormalVideo({ src, desc, href }) {
  return (
    <VideoBox>
      <div className="video-box">
        <iframe
          title={desc}
          src={src}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowfullscreen
        ></iframe>
      </div>
      <ImageDescription>
        <div>
          {href ? (
            <span>
              {' '}
              {`${desc} `}
              <a href={href}>(source)</a>
            </span>
          ) : (
            desc
          )}
        </div>
      </ImageDescription>
    </VideoBox>
  );
}

export function Carousel({ images, texts, urls = [], allowOverflow = true }) {
  const [slideIndex, setSlideIndex] = useState(0);
  const safeImages = Array.isArray(images) ? images : [];

  // Choose appropriate styled components based on allowOverflow parameter
  const CurrentSliderBox = !allowOverflow
    ? styled(SliderBox)`
        max-height: 500px !important;
        aspect-ratio: 16 / 10;
        height: auto;
        width: 100%;
      `
    : SliderBox;

  const CurrentSlidingImageBox = !allowOverflow
    ? styled(SlidingImageBox)`
        height: 100%;
        img {
          object-fit: contain;
          width: 100%;
          height: 100%;
          max-height: 500px;
        }
      `
    : SlidingImageBox;

  const settings = {
    dots: true,
    arrows: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    waitForAnimate: false,
    fade: true,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    beforeChange: (current, next) => setSlideIndex(next),
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    appendDots: dots => (
      <ImageDescription
        style={{
          padding: '10px',
        }}
      >
        <div style={{ paddingTop: 10 }}>
          {urls[slideIndex] ? (
            <span>
              {' '}
              {`${texts[slideIndex]} `}
              <a href={urls[slideIndex]}>(source)</a>
            </span>
          ) : (
            texts[slideIndex]
          )}
        </div>
        <ul style={{ margin: 0, padding: 0, paddingTop: 6 }}> {dots}</ul>
      </ImageDescription>
    ),
    dotsClass: 'slick-dots',
  };

  return (
    <div
      style={{
        boxShadow: '0 8px 16px var(--john-bg-shadow)',
        borderRadius: '12px',
        margin: '2rem auto',
      }}
    >
      <StyledSlider {...settings}>
        {safeImages.map((src, i) => (
          <CurrentSliderBox key={i}>
            <CurrentSlidingImageBox>
              <img src={src} alt={`Slide ${i + 1}`} />
            </CurrentSlidingImageBox>
          </CurrentSliderBox>
        ))}
      </StyledSlider>
    </div>
  );
}

LongImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  desc: PropTypes.string,
  href: PropTypes.string,
};

NormalImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  desc: PropTypes.string,
  href: PropTypes.string,
};

NormalVideo.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  desc: PropTypes.string,
  href: PropTypes.string,
};

Carousel.propTypes = {
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  texts: PropTypes.arrayOf(PropTypes.string).isRequired,
  urls: PropTypes.arrayOf(PropTypes.string),
  allowOverflow: PropTypes.bool,
};

NextArrow.propTypes = {
  onClick: PropTypes.func.isRequired,
};

PrevArrow.propTypes = {
  onClick: PropTypes.func.isRequired,
};
